import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TournamentCard = ({ tournament }) => {
  const navigate = useNavigate();

  const getFormatIcon = (format) => {
    const icons = {
      'T20': 'Zap',
      'ODI': 'Clock',
      'Test': 'Trophy'
    };
    return icons?.[format] || 'Trophy';
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-success/10 text-success',
      'upcoming': 'bg-warning/10 text-warning',
      'completed': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const handleAction = () => {
    if (tournament?.isParticipating) {
      navigate(`/tournament-details/${tournament?.id}`);
    } else {
      navigate(`/tournament-details/${tournament?.id}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntilStart = () => {
    const start = new Date(tournament.startDate);
    const now = new Date();
    const diffTime = start - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden transition-smooth hover:shadow-elevation-lg flex flex-col h-full">
      <div className="relative h-36 md:h-40 overflow-hidden flex-shrink-0">
        <Image
          src={tournament?.image}
          alt={tournament?.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className={`absolute top-2 right-2 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(tournament?.status)}`}>
          {tournament?.status?.charAt(0)?.toUpperCase() + tournament?.status?.slice(1)}
        </div>
      </div>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-base md:text-lg font-semibold text-foreground line-clamp-2 break-words flex-1">
            {tournament?.name}
          </h3>
          <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name={getFormatIcon(tournament?.format)} size={18} color="var(--color-primary)" />
          </div>
        </div>

        <div className="space-y-1.5 mb-3 flex-1">
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span className="break-words">{formatDate(tournament?.startDate)} - {formatDate(tournament?.endDate)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <Icon name="Users" size={14} />
            <span>{tournament?.participants} participants</span>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <Icon name="Target" size={14} />
            <span>{tournament?.predictions ?? 0} predictions</span>
          </div>
          {tournament?.status === 'upcoming' && (
            <div className="flex items-center gap-2 text-xs md:text-sm text-warning">
              <Icon name="Clock" size={14} />
              <span>Starts in {getDaysUntilStart()} days</span>
            </div>
          )}
        </div>

        {(tournament?.prize1 || tournament?.prize2 || tournament?.prize3) && (
          <div className="mb-3 p-2.5 bg-accent/10 rounded-lg space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground flex items-center gap-1">
                <Icon name="Trophy" size={12} />
                1st
              </span>
              <span className="font-semibold text-foreground break-words text-right ml-2">{tournament?.prize1 || 'TBA'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground flex items-center gap-1">
                <Icon name="Award" size={12} />
                2nd
              </span>
              <span className="font-semibold text-foreground break-words text-right ml-2">{tournament?.prize2 || 'TBA'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground flex items-center gap-1">
                <Icon name="Medal" size={12} />
                3rd
              </span>
              <span className="font-semibold text-foreground break-words text-right ml-2">{tournament?.prize3 || 'TBA'}</span>
            </div>
          </div>
        )}

        <Button
          variant={tournament?.isParticipating ? "outline" : "default"}
          fullWidth
          iconName={tournament?.isParticipating ? "Eye" : "UserPlus"}
          iconPosition="left"
          onClick={handleAction}
        >
          {tournament?.isParticipating ? 'View Predictions' : 'Join Tournament'}
        </Button>
      </div>
    </div>
  );
};

export default TournamentCard;